/**
 * usePosteingang / usePosteingangCounts - die Eingangsliste-Reads (epic B2).
 *
 * live -> GET /posteingang, keyset-paged. mock -> the wire-shaped fixture in one page,
 * filtered with the same rules the server applies, so the screen behaves the same on
 * either side of the flip.
 *
 * Paging rule, and it is not the usual one: keep fetching while `next_cursor` is
 * non-null, never stop on a short page. The visibility gate (H4) drops rows the viewer
 * may not see AFTER the limit is applied, so a page of 3 out of a limit of 50 can still
 * have hundreds of Eingaenge behind it. `next_cursor` is the only end-of-list signal.
 *
 * [Source: docs/eingangsverarbeitung-epic.md §3 WS-B B1] (B1 step 3, live since 2026-09-08)
 * [Source: apps/web/src/features/customers/hooks/useKundeRows.ts] (the infinite-query shape)
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  POSTEINGANG_STATE,
  type PosteingangItem,
  type PosteingangKanal,
  type PosteingangListParams,
  type PosteingangCounts,
  type PosteingangListResponse,
  type PosteingangState,
} from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { isLiveData } from '../../../lib/data-mode';
import { qk } from '../../../lib/query-keys';
import { POSTEINGANG_MOCK, eingangAttachmentsMock, mockEingangStatus } from '../mock';
import { extractEmailAddress } from '@brokly/shared';
import { mockPrivateAddresses } from '../../blocklist/useSenderPrivate';

/** The server's own default, mirrored so the mock page reports a truthful `limit`. */
const PAGE_LIMIT = 50;

/** Offen/erledigt tab over the stored status, like the server. */
function matchesState(item: PosteingangItem, state: PosteingangState): boolean {
  return item.eingang_status === state;
}

/** Mock Volltext over the fixture's sender, customer, subject and attachment names. */
function matchesQuery(item: PosteingangItem, q: string | undefined): boolean {
  const needle = q?.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    item.from,
    item.to,
    item.cc,
    item.kunde_name,
    item.subject,
    ...eingangAttachmentsMock(item.id).map((a) => a.filename),
  ];
  return haystack.some((v) => (v ?? '').toLowerCase().includes(needle));
}

/** The mock leg of the list: the same filters the query applies server-side. */
function filterMock(params: PosteingangListParams): PosteingangItem[] {
  const state = params.state ?? POSTEINGANG_STATE.OFFEN;
  // P2 hardening 04: private_senders=true shows ONLY the hidden rows; default hides them.
  const privateAddrs = mockPrivateAddresses();
  const isHidden = (item: PosteingangItem) => {
    const addr = extractEmailAddress(item.from);
    return addr != null && privateAddrs.has(addr);
  };
  return POSTEINGANG_MOCK
    // A status changed in the demo lives in memory (setMockEingangStatus).
    .map((item) => ({ ...item, eingang_status: mockEingangStatus(item.id) }))
    .filter(
      (item) =>
        matchesState(item, state) &&
        (!params.kanal || item.kanal === params.kanal) &&
        matchesQuery(item, params.q) &&
        (!params.attachment_hits || params.attachment_hits.includes(item.id)) &&
        (params.private_senders ? isHidden(item) : !isHidden(item)),
    );
}

/** The whole fixture as one final page - the mock never has a second one. */
function mockPage(params: PosteingangListParams): PosteingangListResponse {
  return { data: filterMock(params), page: { next_cursor: null, limit: PAGE_LIMIT } };
}

export interface PosteingangList {
  data: PosteingangItem[];
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
}

/** The Eingangsliste for one tab + channel filter, paged to the end of the bucket. */
export function usePosteingang(
  state: PosteingangState,
  kanal: PosteingangKanal | null,
  /** The search bar: Volltext (`q`) or the semantic hits (`attachment_hits`). */
  search: Pick<PosteingangListParams, 'q' | 'attachment_hits'> = {},
  /** P2 hardening 04: the "Private Absender" chip — show ONLY the rows hidden for the caller. */
  options: { private_senders?: boolean } = {},
): PosteingangList {
  const live = isLiveData();
  const params: PosteingangListParams = {
    state,
    ...(kanal ? { kanal } : {}),
    ...(search.q ? { q: search.q } : {}),
    ...(search.attachment_hits ? { attachment_hits: search.attachment_hits } : {}),
    ...(options.private_senders ? { private_senders: true } : {}),
  };

  const query = useInfiniteQuery<PosteingangListResponse>({
    queryKey: qk.posteingang({ ...params, mode: live ? 'live' : 'mock' }),
    queryFn: ({ pageParam }) =>
      live
        ? apiClient.posteingang.list({
            ...params,
            ...(pageParam ? { cursor: pageParam as string } : {}),
          })
        : Promise.resolve(mockPage(params)),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.page.next_cursor ?? undefined,
    // mock mode: synchronous initialData (no flicker, deterministic tests). Live mode
    // must NOT seed from the mock — that would flash fabricated rows before the fetch.
    ...(live
      ? {}
      : {
          initialData: { pages: [mockPage(params)], pageParams: [undefined] },
          staleTime: Infinity,
        }),
  });

  return {
    data: query.data?.pages.flatMap((page) => page.data) ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    fetchNextPage: () => void query.fetchNextPage(),
    isFetchingNextPage: query.isFetchingNextPage,
  };
}

/**
 * The mock leg of the counts. Both numbers are derived from the SAME filtered set the
 * list renders, so the badge and the rows can never disagree in mock mode.
 */
function mockCounts(): PosteingangCounts {
  const offen = filterMock({ state: POSTEINGANG_STATE.OFFEN });
  return { offen: offen.length, unread: offen.filter((item) => item.unread === true).length };
}

/**
 * The Posteingang's counts, both of them: `offen` badges the sidebar entry, `unread` is
 * the Eingangsliste's own badge (B2 asks for both). Server-counted over the whole bucket,
 * never derived from what has been paged in - the visibility gate drops rows inside a
 * page, so a client-side count would be short by whatever the viewer may not see.
 *
 * `unread` is «offen AND unread for the calling viewer», so it is always <= `offen`.
 */
export function usePosteingangCounts() {
  const live = isLiveData();
  return useQuery<PosteingangCounts>({
    queryKey: qk.posteingangCounts(live ? 'live' : 'mock'),
    queryFn: () => (live ? apiClient.posteingang.counts() : Promise.resolve(mockCounts())),
    // A badge is the one thing that must never guess: a failed/absent count renders no
    // badge rather than a stale mock number (the useNavCounts «honest or absent» rule).
    retry: false,
    ...(live ? {} : { initialData: mockCounts, staleTime: Infinity }),
  });
}
